"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
  useRef,
} from "react";
import { useSearchParams } from "next/navigation";
import { toast } from "@/hooks/use-toast";
import { useTranslations } from "next-intl";
import { User } from "@/lib/types/api";
import { AuthService } from "@/lib/services/auth-service";
import { ca } from "date-fns/locale";

// Authentication State Interface
export interface AuthState {
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  user: User | null;
  sessionStatus: "loading" | "authenticated" | "unauthenticated";
}

// Public Organization State Interface
export interface PublicOrgState {
  publicOrgId: string | null;
  isLoading: boolean;
  isInPublicOrganization: boolean;
}

// Combined Context State
interface AuthContextState {
  auth: AuthState;
  publicOrg: PublicOrgState;
}

// Context Actions Interface
interface AuthContextActions {
  [x: string]: any;
  refreshAuth: () => Promise<void>;
  clearError: () => void;
  signOut: () => Promise<void>;
}

interface AuthContextType {
  state: AuthContextState;
  actions: AuthContextActions;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Context Provider - Single Source of Truth
 *
 * Single Responsibility: Manage global authentication state
 * Separation of Concerns: Only handles auth state, not UI or routing
 * DRY: Prevents duplicate API calls across components
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const t = useTranslations("LoginForm");
  const searchParams = useSearchParams();

  // Refs to prevent multiple concurrent calls
  const authCheckInProgress = useRef(false);
  const publicOrgFetchInProgress = useRef(false);
  const notificationsHandled = useRef(false);
  const lastAuthCheck = useRef<number>(0);

  // Cache utilities
  const AUTH_CACHE_KEY = "neo_auth_cache";
  const AUTH_CACHE_TTL = 2 * 60 * 1000; // 2 minutes

  const getCachedAuth = (): { user: User; timestamp: number } | null => {
    if (typeof window === "undefined") return null;

    try {
      const cached = sessionStorage.getItem(AUTH_CACHE_KEY);
      if (!cached) return null;

      const { user, timestamp } = JSON.parse(cached);
      const isExpired = Date.now() - timestamp > AUTH_CACHE_TTL;

      if (isExpired) {
        sessionStorage.removeItem(AUTH_CACHE_KEY);
        return null;
      }

      return { user, timestamp };
    } catch {
      return null;
    }
  };

  const setCachedAuth = (user: User): void => {
    if (typeof window === "undefined") return;

    try {
      const cacheData = {
        user,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(cacheData));
    } catch {
      // Ignore storage errors
    }
  };

  const clearCachedAuth = (): void => {
    if (typeof window === "undefined") return;
    try {
      sessionStorage.removeItem(AUTH_CACHE_KEY);
    } catch {
      // Ignore storage errors
    }
  };

  // Authentication state
  const [authState, setAuthState] = useState<AuthState>({
    isLoading: true,
    error: null,
    isAuthenticated: false,
    user: null,
    sessionStatus: "loading",
  });

  // Public organization state
  const [publicOrgState, setPublicOrgState] = useState<PublicOrgState>({
    publicOrgId: null,
    isLoading: false,
    isInPublicOrganization: false,
  });

  // Global auth notifications - run only once
  useEffect(() => {
    if (notificationsHandled.current) return;

    const verified = searchParams.get("verified");
    if (verified === "true") {
      const successDesc = t("emailVerifiedSuccessDescription");
      toast({
        title: t("emailVerifiedSuccessTitle"),
        description: successDesc,
        variant: "success",
      });
      notificationsHandled.current = true;
    }
  }, [searchParams, t]);

  // Centralized auth check - called only once with debouncing
  const checkAuthentication = useCallback(async () => {
    if (authCheckInProgress.current) {
      return;
    }

    // Check if we have recent cached auth data
    const now = Date.now();
    const timeSinceLastCheck = now - lastAuthCheck.current;
    const cached = getCachedAuth();

    if (cached && timeSinceLastCheck < 30000) {
      // 30 seconds
      console.log(cached);
      setAuthState({
        isLoading: false,
        error: null,
        isAuthenticated: true,
        user: cached.user,
        sessionStatus: "authenticated",
      });
      return { isAuth: true, user: cached.user };
    }

    authCheckInProgress.current = true;
    lastAuthCheck.current = now;

    try {
      // Use the optimized method that makes only one API call
      const { isAuthenticated: isAuth, user } =
      await AuthService.authenticateAndGetUser();
      console.log("");

      setAuthState({
        isLoading: false,
        error: null,
        isAuthenticated: isAuth,
        user,
        sessionStatus: isAuth ? "authenticated" : "unauthenticated",
      });

      // Cache user data if authenticated
      if (user) {
        setCachedAuth(user);
      }

      return { isAuth, user };
    } catch (error) {
      console.error("❌ AuthContext: Auth check failed:", error);
      setAuthState({
        isLoading: false,
        error: error instanceof Error ? error.message : "Authentication failed",
        isAuthenticated: false,
        user: null,
        sessionStatus: "unauthenticated",
      });
      clearCachedAuth();
      return { isAuth: false, user: null };
    } finally {
      authCheckInProgress.current = false;
    }
  }, []);

  // Centralized public org fetch - called only once when authenticated with debouncing
  const fetchPublicOrganization = useCallback(
    async (isAuthenticated: boolean, userId?: string) => {
      if (!isAuthenticated) {
        setPublicOrgState({
          publicOrgId: null,
          isLoading: false,
          isInPublicOrganization: false,
        });
        return;
      }

      if (publicOrgFetchInProgress.current) {
        return;
      }

      publicOrgFetchInProgress.current = true;
      setPublicOrgState(prev => ({ ...prev, isLoading: true }));

      try {
        const { UserApiClient } = await import(
          "@/lib/api/clients/user-api-client"
        );
        const userApiClient = new UserApiClient();
        const publicOrg = await userApiClient.getPublicOrganization();

        setPublicOrgState({
          publicOrgId: publicOrg.id,
          isLoading: false,
          isInPublicOrganization: false, // Will be calculated in context value
        });
      } catch (error) {
        // Silently fail - public org might not be enabled
        setPublicOrgState({
          publicOrgId: null,
          isLoading: false,
          isInPublicOrganization: false,
        });
      } finally {
        publicOrgFetchInProgress.current = false;
      }
    },
    []
  );

  const updateUser = useCallback((partial: Partial<User>) => {
  setAuthState(prev => ({
    ...prev,
    user: prev.user ? { ...prev.user, ...partial } : prev.user,
  }));
  const cached = getCachedAuth();
  if (cached?.user) setCachedAuth({ ...cached.user, ...partial });
}, []);

  // Initialize authentication on mount - ONLY ONCE
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      if (!mounted) return;

      // Debug localStorage tokens
      if (typeof window !== "undefined") {
        const accessToken = localStorage.getItem("auth_access_token");
        const user = localStorage.getItem("auth_user");
        const expiry = localStorage.getItem("auth_token_expiry");
      }

      await checkAuthentication();
    };

    initAuth();

    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Fetch public org when authentication status changes - ONLY when needed
  useEffect(() => {
    if (authState.sessionStatus === "authenticated" && authState.user) {
      fetchPublicOrganization(true, authState.user.id);
    } else if (authState.sessionStatus === "unauthenticated") {
      fetchPublicOrganization(false);
    }
  }, [authState.sessionStatus, authState.user?.id, fetchPublicOrganization]);

  // Actions with stable references
  const refreshAuth = useCallback(async () => {
    await checkAuthentication();
  }, [checkAuthentication]);

  const clearError = useCallback(() => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await AuthService.signOut();
      setAuthState({
        isLoading: false,
        error: null,
        isAuthenticated: false,
        user: null,
        sessionStatus: "unauthenticated",
      });
      setPublicOrgState({
        publicOrgId: null,
        isLoading: false,
        isInPublicOrganization: false,
      });
      clearCachedAuth();

      // Clear user context cache
      const { UserContextService } = await import(
        "@/lib/services/user-context-service"
      );
      UserContextService.clearUserIdCache();
    } catch (error) {
      console.error("Sign out error:", error);
      setAuthState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : "Sign out failed",
      }));
    }
  }, []);

  // Memoized context value with computed isInPublicOrganization
  const contextValue = useMemo(() => {
    const isInPublicOrg = !!(
      authState.user?.organization_id &&
      publicOrgState.publicOrgId &&
      authState.user.organization_id === publicOrgState.publicOrgId
    );

    return {
      state: {
        auth: authState,
        publicOrg: {
          ...publicOrgState,
          isInPublicOrganization: isInPublicOrg,
        },
      },
      actions: {
        refreshAuth,
        updateUser,
        clearError,
        signOut,
      },
    };
  }, [authState, publicOrgState, refreshAuth, clearError, signOut, updateUser]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}
